import { useEffect, useState } from 'react';
import { getAllCartItems, CART_CHANGED_EVENT } from '../services/cart/cartService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Number of items in the cart (sum of quantities), for the cart badges in the
 * nav and the mobile tab bar.
 *
 * One shared store for the whole page rather than a fetch per component: Home
 * renders two navs (desktop / mobile trees), and every badge should show the
 * same number from one request. Refreshes are coalesced, and a response that
 * arrives after a newer request was started is dropped.
 *
 * It refreshes when the cart service announces a change (add, update, remove,
 * clear, the login merge), when the auth state flips (guest cart ↔ account
 * cart), when another tab edits the guest cart, and when the window regains
 * focus (the cart may have changed on another device).
 */
let count = 0;
let seq = 0;
let timer = null;
const listeners = new Set();

const publish = (next) => {
  count = next;
  listeners.forEach((fn) => fn(next));
};

const load = async () => {
  const mine = ++seq;
  try {
    const res = await getAllCartItems();
    const cart = res?.data?.cart || [];
    const next = cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    if (mine === seq) publish(next);
  } catch {
    if (mine === seq) publish(0);
  }
};

/** Coalesce bursts (two navs mounting, several events) into one request. */
export const refreshCartCount = () => {
  clearTimeout(timer);
  timer = setTimeout(load, 30);
};

const onStorage = (e) => {
  if (!e.key || e.key === 'guestCart') refreshCartCount();
};

const subscribe = (fn) => {
  listeners.add(fn);
  if (listeners.size === 1) {
    window.addEventListener(CART_CHANGED_EVENT, refreshCartCount);
    window.addEventListener('focus', refreshCartCount);
    window.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      window.removeEventListener(CART_CHANGED_EVENT, refreshCartCount);
      window.removeEventListener('focus', refreshCartCount);
      window.removeEventListener('storage', onStorage);
    }
  };
};

export default function useCartCount() {
  const { isLoggedIn } = useAuth();
  const [value, setValue] = useState(count);

  useEffect(() => subscribe(setValue), []);
  useEffect(() => {
    refreshCartCount();
  }, [isLoggedIn]);

  return value;
}
